/**
 * @author jakubbujakowski
 * @since 2020-12-3
 */

import React, { ReactElement } from "react";
import styled from "styled-components";
import { NavLink as Link } from "react-router-dom";
import { format } from "date-fns";

import { EMPLOYEE_STATUS } from "@typings/contracts";

import theme from "@ui/themes/default";
import { Caret } from "@ui/Assets/Symbolicons/Caret";
import { History } from "@ui/Assets/Symbolicons/History";
import { Flag } from "@ui/Assets/Symbolicons/Flag";
import { CheckCircle } from "@ui/Assets/Symbolicons/CheckCircle";
import { Phone } from "@ui/Assets/Symbolicons/Phone";
import { OptionPanel, RoundPicture, Paragraph } from "@ui/Atoms";
import { Row, Column } from "@ui/Atoms/Grid";

import { DATE_FORMAT } from "@services/DateFormatter";

interface Employee {
  employeeName: string | null;
  employeePhoneNumber: string | null;
  employeeProfilePictureUrl: string | null;
  status: EMPLOYEE_STATUS;
  statusLabel: string;
  statusDate: string;
}

interface Props {
  url: string;
  employee: Employee;
  isMobile: boolean;
}

const MyEmployeeCard = (props: Props): ReactElement => {
  const iconSize = props.isMobile ? 38 : 44;

  const getIcon = (status: EMPLOYEE_STATUS) => {
    const iconMap: Record<EMPLOYEE_STATUS, React.ReactElement> = {
      [EMPLOYEE_STATUS.ACTIVE]: (
        <CheckCircle size={iconSize} color={theme.palette.green500} />
      ),
      [EMPLOYEE_STATUS.PENDING]: (
        <Flag size={iconSize} color={theme.palette.blue500} />
      ),
      [EMPLOYEE_STATUS.TERMINATED]: <History size={iconSize} />,
    };
    return iconMap[status];
  };

  return (
    <Link
      to={props.url}
      onClick={e =>
        props.employee.status === EMPLOYEE_STATUS.PENDING && e.preventDefault()
      }
    >
      <StyledOptionPanel
        isPending={props.employee.status === EMPLOYEE_STATUS.PENDING}
      >
        <Row>
          {/** Picture Column */}
          <Column xs={3} sm={2} md={2} lg={1}>
            <RoundPicture
              shrinkOnMobile
              enlargeOnTabletUp
              picturePath={props.employee.employeeProfilePictureUrl}
            />
          </Column>

          {/** Employee Info Column */}
          <ColumnNoGutterMobile xs={7} sm={8} md={8} lg={9}>
            <Row>
              <Column lg={6} xl={5}>
                <EmployeeInfoWrapper>
                  <ParagraphStrong type="body">
                    {props.employee.employeeName}
                  </ParagraphStrong>
                  <StyledParagraph type="body2">
                    <IconWrapper>{getIcon(props.employee.status)}</IconWrapper>
                    {props.employee.statusDate && (
                      <span>
                        {props.employee.statusLabel}{" "}
                        {format(
                          new Date(props.employee.statusDate),
                          DATE_FORMAT.DAY_PICKER_INPUT,
                        )}
                      </span>
                    )}
                  </StyledParagraph>
                </EmployeeInfoWrapper>
              </Column>
              <ColumnAlignBottomDesktop lg={6} xl={7}>
                <StyledParagraph type="body2">
                  <IconWrapper>
                    <Phone size={iconSize} />
                  </IconWrapper>
                  <span>{props.employee.employeePhoneNumber}</span>
                </StyledParagraph>
              </ColumnAlignBottomDesktop>
            </Row>
          </ColumnNoGutterMobile>

          {/** Navigation Column */}
          <ColumnNav xs={2}>
            {props.employee.status !== EMPLOYEE_STATUS.PENDING && (
              <CaretWrapper>
                <Caret size={40} color={theme.palette.blue500} variant="line" />
              </CaretWrapper>
            )}
          </ColumnNav>
        </Row>
      </StyledOptionPanel>
    </Link>
  );
};

MyEmployeeCard.displayName = "MyEmployeeCard";

export { MyEmployeeCard };

const ColumnNoGutterMobile = styled(Column)`
  ${props => props.theme.breakpoints.upToTablet} {
    padding-left: 0;
    padding-right: 0;
  }
`;

const ColumnAlignBottomDesktop = styled(Column)`
  ${props => props.theme.breakpoints.desktop} {
    display: flex;
    align-items: flex-end;
  }
`;

const ColumnNav = styled(Column)`
  ${props => props.theme.breakpoints.desktop} {
    display: flex;
    align-items: center;
  }
`;

const ParagraphStrong = styled(Paragraph)`
  font-weight: ${props => props.theme.fonts.weights.bold};
`;

const CaretWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;

  svg {
    transform: rotate(-90deg);
  }
`;

const IconWrapper = styled.span`
  svg {
    padding-right: ${props => props.theme.margins.base};
    margin-left: -${props => props.theme.margins.half};

    ${props => props.theme.breakpoints.desktop} {
      margin-left: -${props => props.theme.margins.base};
    }
  }
`;

const StyledParagraph = styled(Paragraph)`
  display: flex;
  align-items: center;
`;

const StyledOptionPanel = styled(OptionPanel)<{ isPending: boolean }>`
  padding: ${props => props.theme.margins.base_x2};

  ${props =>
    props.isPending &&
    `
    cursor: default;
  `}
`;

const EmployeeInfoWrapper = styled.div`
  ${props => props.theme.breakpoints.desktop} {
    padding-left: ${props => props.theme.margins.base_x4};
  }

  ${props => props.theme.breakpoints.desktopLarge} {
    padding-left: 0;
  }
`;
